import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Table, Typography, Button, Space, Layout, Card, Statistic, Row, Col,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;
const { Content } = Layout;

const fmtMoney = (v) => v != null ? `£${Number(v).toFixed(2)}` : '—';

const DriverRunsDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isOpcUser } = useAuth();

  const driverName  = searchParams.get('name') || 'Driver';
  const start       = searchParams.get('start');
  const end         = searchParams.get('end');
  const liteCovered = searchParams.get('liteCovered') === 'true';
  const driverId    = searchParams.get('driverId');

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!start || !end) return;
    setLoading(true);
    const params = { startDate: start, endDate: end };
    if (liteCovered) params.liteCovered = true;
    else if (driverId) params.driver = driverId;

    apiService.dailyRuns.getAll(params)
      .then(res => setRuns(res.data || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [start, end, liteCovered, driverId]);

  const totalEarnings = runs.reduce((s, r) => s + (isOpcUser ? (r.opcShare || 0) : (r.total || 0)), 0);

  // Group runs by date — one row per day worked
  const byDay = runs.reduce((acc, r) => {
    const dk = dayjs(r.date).format('YYYY-MM-DD');
    if (!acc[dk]) acc[dk] = { date: dk, runs: 0, net: 0, earnings: 0 };
    acc[dk].runs += 1;
    acc[dk].net += r.net || 0;
    acc[dk].earnings += isOpcUser ? (r.opcShare || 0) : (r.total || 0);
    return acc;
  }, {});
  const dayRows = Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  const uniqueDays = dayRows.map(d => d.date);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 160,
      render: (d) => dayjs(d).format('ddd DD MMM YYYY'),
      sorter: (a, b) => a.date.localeCompare(b.date),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Runs',
      dataIndex: 'runs',
      key: 'runs',
      align: 'center',
      width: 80,
    },
    {
      title: 'Net',
      dataIndex: 'net',
      key: 'net',
      align: 'right',
      render: fmtMoney,
      sorter: (a, b) => a.net - b.net,
    },
    {
      title: isOpcUser ? 'Your Share' : 'Total',
      dataIndex: 'earnings',
      key: 'earnings',
      align: 'right',
      render: fmtMoney,
      sorter: (a, b) => a.earnings - b.earnings,
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 24 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
            <div>
              <Title level={3} style={{ margin: 0 }}>{driverName}</Title>
              <Text type="secondary">
                {start && end ? `${dayjs(start).format('DD MMM YYYY')} – ${dayjs(end).format('DD MMM YYYY')}` : ''}
              </Text>
            </div>
          </div>

          {/* Summary stats */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic title="Days Worked" value={uniqueDays.length} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic title="Total Runs" value={runs.length} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic
                  title={isOpcUser ? 'Your Earnings' : 'Total Earnings'}
                  value={fmtMoney(totalEarnings)}
                />
              </Card>
            </Col>
          </Row>

          {/* Runs table */}
          <Card title={`Runs (${dayRows.length})`}>
            <Table
              dataSource={dayRows}
              columns={columns}
              rowKey="date"
              loading={loading}
              pagination={{ pageSize: 50, hideOnSinglePage: true }}
              size="small"
              bordered
            />
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default DriverRunsDetail;
